import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '../../stores/store';
import { logIn, tokenExpired } from '../../stores/slices/current-user-slice';
import { mutex } from '../../stores/base/custom-slice';

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_SERVER_URL}/api/`,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(addAuthorizationHeader, handleError);
axiosInstance.interceptors.request.use(handleRequest, handleError);
axiosInstance.interceptors.response.use(handleSuccess, handleError);

function addAuthorizationHeader(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = sessionStorage.getItem('token');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}

function handleRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  // Modify or add any request configurations here
  return config;
}

function handleSuccess(response: AxiosResponse): AxiosResponse {
  // Handle successful response here
  return response;
}

async function handleError(error: AxiosError) : Promise<any> {
  if (error.response?.status === 401) {
    return await handle401Error(error);
  }

  return Promise.reject(error);
}

async function handle401Error(error: AxiosError<unknown, any>) {
  let result: any;

  if (!mutex.isLocked()) {
    const release = await mutex.acquire();

    try {

      const refreshResult = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/authentication/refresh`, {
        refreshToken: sessionStorage.getItem('refreshToken'),
      });

      if (refreshResult.data) {
        store.dispatch(logIn(refreshResult.data));
        const originalRequest = error.config as any;
        originalRequest.headers.Authorization = `Bearer ${refreshResult.data.token}`;
        result = await axiosInstance.request(originalRequest);
      } else {
        result = handleTokenExpiration();
      }
    }
    catch (error) {
      result = handleTokenExpiration();
    }
    finally {
      release();
    }
  } else {
    await mutex.waitForUnlock();
    result = axiosInstance.request(error.config as any);

    if (result.error && result.error.status === 401) {
      result = handleTokenExpiration();
    }
  }

  return result;
}

function handleTokenExpiration() {
  store.dispatch(tokenExpired());
  return new Promise(() => { }) as never;
}

class HttpService {
  private controllerName : string;

  constructor(controllerName: string) {
    this.controllerName = controllerName;
  }

  public get(url: string = '', config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
    return axiosInstance.get(`${this.controllerName}/${url}`, config);
  }

  public post(url: string = '', data?: any, config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
    return axiosInstance.post(`${this.controllerName}/${url}`, data, config);
  }

  public put(url: string = '', data?: any, config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
    return axiosInstance.put(`${this.controllerName}/${url}`, data, config);
  }

  public delete(url: string = '', config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
    return axiosInstance.delete(`${this.controllerName}/${url}`, config);
  }
}

export default HttpService;